/**
 * Field order inside one card (brief §14, ADR-066).
 *
 * ADR-066 Decision 1 makes the field with the lowest stored position
 * authoritative for its kind, and Decision 4 makes that order the user's to
 * set. So this is not cosmetic arrangement arithmetic: a move that changes
 * which Definition comes first changes what Learn asks. The three functions
 * here are the whole of the semantics, kept out of the component so they can be
 * asserted directly — the drag itself is browser geometry and cannot be, the
 * same split `deckFocusPositioning` already uses.
 *
 * Order is expressed here as a SEQUENCE, never as a set of absolute positions.
 * That is deliberate and it is the same correction commit 4c1c7f5 made for card
 * order: a caller that computes `{id, position}` pairs is claiming something
 * about rows it may not have. It happens not to be able to go wrong for fields
 * — a card always carries all of its sides, so there is no partial view to
 * mistake for the whole — but the write path stays the same shape regardless.
 * `PATCH /cards/{id}` takes the ordered side list and the SERVER assigns
 * `position = i` over it (`_build_sides`); no position integer is ever sent.
 */

/** The part of a field draft this module reasons about. */
export interface OrderedField {
  key: string
  kind: string
}

/** The part of a row's geometry the drop rule reads. `null` for a row whose
 * element is not currently mounted or measurable — treated as "no neighbour
 * there", so a missing rect stalls the move rather than guessing at one. */
export type RowSpan = { top: number; bottom: number } | null

/** A row exactly as the DOM reports it: the rect `getBoundingClientRect`
 * returns — which INCLUDES any transform currently applied — plus the vertical
 * translation responsible for the difference. */
export type MeasuredRow = { top: number; bottom: number; translateY: number } | null

/**
 * Settled layout spans, with any in-flight slide animation subtracted.
 *
 * This exists because of a defect, and the defect is worth stating rather than
 * just guarded against. `stepTowardPointer` decides by neighbours' midpoints,
 * and its two conditions are exact complements *of the same geometry* — that is
 * what makes it non-oscillating, and what its tests assert. The complementarity
 * says nothing at all if the two samples are measured against DIFFERENT
 * geometry.
 *
 * That is precisely what a reorder produces once the rows animate. A swap
 * starts a FLIP slide on every displaced row, so for the next 150ms
 * `getBoundingClientRect()` reports each of them partway between where it was
 * and where it is going. A neighbour still sliding down into place is DRAWN
 * above its final position, so its midpoint is lower than its settled midpoint,
 * so the reverse step qualifies against a finger that has not moved at all —
 * and since the reverse step starts a fresh slide, the sample after that is
 * mid-flight again in the other direction. The result on device is a burst of
 * swaps from a stationary hold.
 *
 * The rule was never wrong and is not loosened here: no hysteresis band, no
 * dead zone, no settling delay. The input is corrected to be what the rule
 * always assumed it was — where the rows actually ARE, not where they are
 * currently being painted. A drag consequently decides against the same
 * geometry whether or not the previous step is still animating, which is also
 * why nothing here has to know how long the animation lasts.
 */
export function settledSpans(rows: readonly MeasuredRow[]): RowSpan[] {
  return rows.map((r) => (
    r ? { top: r.top - r.translateY, bottom: r.bottom - r.translateY } : null
  ))
}

/**
 * Row spans widened into the SLOTS they occupy, so that adjacent spans share a
 * boundary instead of being separated by empty space.
 *
 * `stepTowardPointer`'s two conditions are exact complements only if the spans
 * TILE. Border boxes do not: the active card is a flex column with an 11px
 * `gap`, so between every pair of rows there are 11 pixels belonging to no
 * row's span at all. Working that through for neighbours A (above) and the
 * dragged row B, with gap G:
 *
 *     swap up   requires  virtualTop < mid(A) = hA/2
 *     and once swapped, A sits at [hB + G, …], so
 *     swap down requires  virtualTop > hA/2 + G
 *
 * — a G-wide band in which NEITHER fires. That is not oscillation (nothing
 * fires twice, which is why the oscillation fix is unaffected); it is a dead
 * band that makes the gesture one-way. Crossing a boundary swaps, and bringing
 * the finger back to the very place it swapped at does not swap back, because
 * the return threshold now sits a whole gap further on. Measured as exactly
 * 11px on every row-height combination — equal, tall-over-short,
 * short-over-tall, and a row between two others — because it is the gap, and
 * the gap does not depend on the heights.
 *
 * Widening each span by half a gap on each side restores tiling. Two properties
 * make it the right correction rather than a nudge:
 *
 *  - It does not move any midpoint. A symmetric expansion leaves `(top+bottom)/2`
 *    exactly where it was, so the thresholds this rule compares against are the
 *    ones it always compared against; what changes is only that the space
 *    between rows now belongs to one row or the other rather than to neither.
 *  - It gives every row the same height increase (one full gap), including the
 *    first and last, whose outer edges are extended by the same half gap even
 *    though nothing abuts them. The dragged row's own height is therefore the
 *    same wherever it currently sits in the stack, which is what lets a single
 *    `grabOffset` captured at drag start stay correct for the whole gesture.
 *
 * Gaps are read per boundary from the measured rows rather than taken as a
 * constant, so this cannot drift out of step with the card's CSS. A pair with
 * an unmeasurable row shares no boundary and is left alone — the drop rule
 * already treats a missing neighbour as no neighbour.
 */
export function tileSpans(rows: readonly RowSpan[]): RowSpan[] {
  const out: RowSpan[] = rows.map((r) => (r ? { top: r.top, bottom: r.bottom } : null))
  let firstHalf: number | null = null
  let lastHalf: number | null = null
  // Boundaries are computed from the ORIGINAL spans; `out` is only written to,
  // never read back, so an already-moved edge cannot feed the next boundary.
  for (let i = 0; i < rows.length - 1; i++) {
    const above = rows[i]
    const below = rows[i + 1]
    if (!above || !below) continue
    const half = (below.top - above.bottom) / 2
    const boundary = above.bottom + half
    out[i]!.bottom = boundary
    out[i + 1]!.top = boundary
    if (firstHalf === null) firstHalf = half
    lastHalf = half
  }
  const head = out[0]
  const tail = out[out.length - 1]
  if (head && firstHalf !== null) head.top -= firstHalf
  if (tail && lastHalf !== null) tail.bottom += lastHalf
  return out
}

/** `from` out of the array and back in at `to`, order otherwise untouched. */
export function moveField<T>(fields: readonly T[], from: number, to: number): T[] {
  const next = [...fields]
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return next
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * Where the dragged row belongs after this pointer sample: its own index, or
 * one step towards the pointer.
 *
 * The row is judged by where the AUTHOR IS CARRYING IT, not by where the layout
 * currently has it. `grabOffset` — how far down the row the handle was pressed
 * — turns the pointer back into a virtual top edge, and the row's own height
 * gives the bottom. It moves up when that virtual top passes the midpoint of
 * the row above, and down when the virtual bottom passes the midpoint of the
 * row below.
 *
 * Ignoring the offset is the obvious implementation and it is wrong on this
 * surface specifically. The handle sits in the label band at the TOP of a row,
 * and a side-B row is as tall as its content (§8) — a two-line Example is four
 * times a Translation. Comparing the raw pointer against the next row's
 * midpoint would make dragging a tall field down over a short one cost a
 * gesture longer than both rows put together, because the finger would have to
 * reach a midpoint the row's own body had already passed.
 *
 * The two conditions are exact complements, which is what makes this stable
 * with unequal heights rather than merely usually stable: for neighbours A
 * (height hA) and the dragged row B, `virtualBottom > midpoint(A below)` after
 * a swap reads `virtualTop > top + hA/2`, the negation of the swap-up test.
 * A sample that swapped cannot swap back on the next sample from an unmoved
 * finger, so the row cannot flicker between two slots.
 *
 * One step per sample. The clamp at both ends is the whole of the edge
 * behaviour: past the first row the answer is the first row, past the last it
 * is the last. Nothing scrolls — see the drag handler in ActiveVocabCard for
 * why that is a decision and not an omission (ADR-067 Decision D).
 */
export function stepTowardPointer(
  rows: readonly RowSpan[],
  index: number,
  pointerY: number,
  grabOffset: number,
): number {
  if (index < 0 || index >= rows.length) return index
  const self = rows[index]
  if (!self) return index
  const virtualTop = pointerY - grabOffset
  const virtualBottom = virtualTop + (self.bottom - self.top)

  const above = index > 0 ? rows[index - 1] : null
  if (above && virtualTop < (above.top + above.bottom) / 2) return index - 1
  const below = index < rows.length - 1 ? rows[index + 1] : null
  if (below && virtualBottom > (below.top + below.bottom) / 2) return index + 1
  return index
}

/**
 * Kinds whose AUTHORITATIVE field changed between two orders — the reorders
 * that change what Learn asks (ADR-066 Decision 1), as opposed to the ones that
 * merely rearrange the card.
 *
 * A kind present once cannot qualify however far its field moves: it is its own
 * first field before and after. Only a repeated kind can hand authority over,
 * which is why the count is checked and not just the identity of the head.
 *
 * Kinds are returned in the order they first appear in `after`, so a caller
 * naming them says them in the order the author is looking at.
 */
export function authorityChanges(
  before: readonly OrderedField[],
  after: readonly OrderedField[],
): string[] {
  const firstOf = (fields: readonly OrderedField[], kind: string) =>
    fields.find((f) => f.kind === kind)?.key ?? null
  const countOf = (fields: readonly OrderedField[], kind: string) =>
    fields.reduce((n, f) => (f.kind === kind ? n + 1 : n), 0)

  const changed: string[] = []
  const seen = new Set<string>()
  for (const f of after) {
    if (seen.has(f.kind)) continue
    seen.add(f.kind)
    // Repeated in EITHER order: a move that also deleted the duplicate is not
    // this module's concern, but a kind that was repeated before and is not now
    // still had an authority to lose.
    if (countOf(after, f.kind) < 2 && countOf(before, f.kind) < 2) continue
    if (firstOf(before, f.kind) !== firstOf(after, f.kind)) changed.push(f.kind)
  }
  return changed
}
